/**
 * OrbisSpinner — 3D planetary orbital loading animation
 *
 * Three rings — outer circle, inner circle, equatorial ellipse — rotating on
 * independent axes, with a pulsing center dot. Proportions derived exactly
 * from the Orbis mark SVG (56px viewbox: r=22, r=13.5, rx=22/ry=8.5, r=5).
 *
 * Usage:
 *   <OrbisSpinner />                          // 64px, warm white
 *   <OrbisSpinner size={32} />
 *   <OrbisSpinner size={120} color="#B8CCDF" />
 */
export default function OrbisSpinner({ size = 64, color = '#F0EFE8' }) {
  // Exact ratios from the Orbis mark 56px viewbox
  const R = {
    outerD:  44 / 56,   // outer circle  r=22  → diam=44
    innerD:  27 / 56,   // inner circle  r=13.5 → diam=27
    equatW:  44 / 56,   // equatorial    rx=22  → w=44
    equatH:  17 / 56,   // equatorial    ry=8.5 → h=17
    coreD:   10 / 56,   // center dot    r=5   → diam=10
    stroke: 0.9 / 56,   // stroke-width  0.9px at 56px
  };

  const outerD = size * R.outerD;
  const innerD = size * R.innerD;
  const equatW = size * R.equatW;
  const equatH = size * R.equatH;
  const coreD  = size * R.coreD;
  const sw     = Math.max(0.6, size * R.stroke);

  // Centering offsets: (containerSize − elementSize) / 2
  const outerOff  = (size - outerD)  / 2;
  const innerOff  = (size - innerD)  / 2;
  const equatOffX = (size - equatW)  / 2;
  const equatOffY = (size - equatH)  / 2;

  // rgba helper — supports both hex (#F0EFE8) and CSS variables (var(--orbis-blue))
  const isVar = color.startsWith('var(');
  const c = (a) => {
    if (isVar) {
      // CSS variables can't be decomposed to rgba at render time,
      // so we use opacity on the element instead
      return color;
    }
    const h = color.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };

  // For CSS variable colors, we use the color directly and control opacity per element
  const opacityStyle = (alpha) => isVar ? { opacity: alpha } : {};

  const uid = `os-${size}-${color.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <>
      <style>{`
        @keyframes ${uid}-rx {
          from { transform: rotateX(0deg);   }
          to   { transform: rotateX(360deg); }
        }
        @keyframes ${uid}-ry {
          from { transform: rotateY(0deg);   }
          to   { transform: rotateY(360deg); }
        }
        @keyframes ${uid}-rxy {
          0%   { transform: rotateX(0deg)   rotateY(0deg);   }
          50%  { transform: rotateX(90deg)  rotateY(180deg); }
          100% { transform: rotateX(360deg) rotateY(360deg); }
        }
        @keyframes ${uid}-pulse {
          0%, 100% { transform: translate(-50%,-50%) scale(1);    }
          50%       { transform: translate(-50%,-50%) scale(1.15); }
        }
      `}</style>

      <div
        role="status"
        aria-label="Loading"
        style={{
          position: 'relative',
          width: size,
          height: size,
          perspective: `${size * 5.33}px`,
          transformStyle: 'preserve-3d',
          flexShrink: 0,
        }}
      >
        {/* Outer circle — rotates X, 2.5s */}
        <div style={{
          position: 'absolute',
          width: outerD, height: outerD,
          top: outerOff, left: outerOff,
          border: `${sw}px solid ${c(0.65)}`,
          borderRadius: '50%',
          boxShadow: isVar ? 'none' : `0 0 ${size * 0.10}px ${c(0.22)}`,
          transformStyle: 'preserve-3d',
          animation: `2.5s ${uid}-rx cubic-bezier(0.65,0,0.35,1) infinite`,
          ...opacityStyle(0.65),
        }} />

        {/* Inner circle — rotates Y, 2s */}
        <div style={{
          position: 'absolute',
          width: innerD, height: innerD,
          top: innerOff, left: innerOff,
          border: `${sw}px solid ${c(0.45)}`,
          borderRadius: '50%',
          boxShadow: isVar ? 'none' : `0 0 ${size * 0.07}px ${c(0.15)}`,
          transformStyle: 'preserve-3d',
          animation: `2s ${uid}-ry cubic-bezier(0.55,0,0.45,1) infinite`,
          ...opacityStyle(0.45),
        }} />

        {/* Equatorial ellipse — rotates X+Y, 3s */}
        <div style={{
          position: 'absolute',
          width: equatW, height: equatH,
          top: equatOffY, left: equatOffX,
          border: `${sw}px solid ${c(0.55)}`,
          borderRadius: '50%',
          boxShadow: isVar ? 'none' : `0 0 ${size * 0.08}px ${c(0.18)}`,
          transformStyle: 'preserve-3d',
          animation: `3s ${uid}-rxy cubic-bezier(0.7,0,0.3,1) infinite`,
          ...opacityStyle(0.55),
        }} />

        {/* Center dot — pulsing core */}
        <div style={{
          position: 'absolute',
          width: coreD, height: coreD,
          top: '50%', left: '50%',
          background: isVar ? color : `radial-gradient(circle at 38% 38%, ${color}, ${c(0.5)})`,
          borderRadius: '50%',
          boxShadow: isVar ? 'none' : `0 0 ${size * 0.12}px ${c(0.40)}`,
          animation: `2s ${uid}-pulse cubic-bezier(0.5,0,0.5,1) infinite`,
          ...opacityStyle(0.9),
        }} />
      </div>
    </>
  );
}
